import { type FC, useState } from "react"
import { useDropzone } from "react-dropzone"
import styled from "styled-components"

import { type CommunityPostCategory, type RawCommunityPost } from "@forento/core/models/community"
import { removeArrayIndex } from "@forento/core/utilities/array"
import { getDataUrlByFile, getExtensionByFileName, supportedFileExtensions } from "@forento/core/utilities/file"
import { parseIntNumber } from "@forento/core/utilities/number"
import {
	getVimeoIdFromUrl,
	getVimeoUrlFromId,
	getYouTubeIdFromUrl,
	getYouTubeUrlFromId,
} from "@forento/core/utilities/url"
import Form from "@forento/ui/components/Form"
import { useFileUrl } from "@forento/ui/hooks/useFileUrl"

import Button, { SubmitButton } from "~/components/Button"
import Dropdown from "~/components/Dropdown"
import { DeleteIcon, UploadFileIcon, UploadIcon } from "~/components/Icon"
import InputField, { useRichTextArea } from "~/components/InputField"
import InputLabel from "~/components/InputLabel"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"
import trpc, { query } from "~/utilities/trpc"

type Props = { postId: number; isOpen: boolean; onClose(): void; onReloadPosts(): Promise<unknown> }
const EditModal: FC<Props> = ({ postId, isOpen, onClose, onReloadPosts }) => {
	const post = query.community.getRawPost.useQuery(postId, { enabled: isOpen })
	const categories = query.community.listPostCategories.useQuery(undefined, { enabled: isOpen })

	if (post.data === null) {
		onClose()
		return
	}

	return (
		<DefaultModal onClose={onClose} isOpen={isOpen} width={800}>
			<ModalTitle>Edit post</ModalTitle>
			{post.error || categories.error ? (
				<>
					<ModalText>Failed to load post. Please try again.</ModalText>
					<ModalButtons>
						<Button variant="primary" onClick={onClose}>
							Close
						</Button>
					</ModalButtons>
				</>
			) : post.data === undefined || categories.data === undefined ? (
				<>
					<PartialLoadingPage />
					<ModalButtons>
						<Button variant="primary" onClick={onClose}>
							Close
						</Button>
					</ModalButtons>
				</>
			) : (
				<Content
					post={post.data}
					categories={categories.data}
					onClose={onClose}
					onReload={post.refetch}
					onReloadPosts={onReloadPosts}
					onReloadCategories={categories.refetch}
				/>
			)}
		</DefaultModal>
	)
}

type ContentProps = {
	post: RawCommunityPost
	categories: CommunityPostCategory[]
	onClose(): void
	onReload(): Promise<unknown>
	onReloadPosts(): Promise<unknown>
	onReloadCategories(): Promise<unknown>
}
const Content: FC<ContentProps> = ({ post, categories, onClose, onReload, onReloadPosts, onReloadCategories }) => {
	const alert = useAlert()

	const [title, setTitle] = useState(post.title)
	const content = useRichTextArea({ label: "Text", initialValue: post.content })
	const [existingImages, setExistingImages] = useState(post.images)
	const [uploadedImages, setUploadedImages] = useState<File[]>([])
	const [videoUrl, setVideoUrl] = useState(
		post.video?.type === "youtube"
			? getYouTubeUrlFromId(post.video.id)
			: post.video?.type === "vimeo"
				? getVimeoUrlFromId(post.video.id)
				: "",
	)
	const [categoryId, setCategoryId] = useState<string | null>(post.categoryId?.toString() ?? null)
	const [removeAttachments, setRemoveAttachments] = useState<number[]>([])
	const [addAttachments, setAddAttachments] = useState<{ label: string; file: File }[]>([])

	const imagesDropzone = useDropzone({
		accept: Object.fromEntries(supportedFileExtensions.image.map(x => [`image/${x.toLowerCase()}`, []])),
		onDrop: files => setUploadedImages(current => [...current, ...files]),
	})

	const save = query.community.updatePost.useMutation({
		async onSuccess() {
			await Promise.all([onReload(), onReloadPosts()])
			onClose()
		},
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Failed to save post. Please try again.")
		},
	})

	const dropzone = useDropzone({
		onDrop: async files => {
			for (const file of files) {
				const dialog = await alert.input(
					"Add attachment",
					`Please enter a label for the file ${file.name}.`,
					"Label",
					file.name,
				)
				if (dialog.result === null) continue
				dialog.close()
				setAddAttachments(current => [...current, { label: dialog.result, file }])
			}
		},
	})

	async function handleSubmit() {
		const contentString = await content.exportEditorState()
		if (contentString === null) return

		save.mutate({
			id: post.id,
			data: {
				title,
				content: contentString,
				categoryId: parseIntNumber(categoryId),
				youtubeId: getYouTubeIdFromUrl(videoUrl.trim()),
				vimeoId: getVimeoIdFromUrl(videoUrl.trim()),
				addImageDataUrls: await Promise.all(uploadedImages.map(getDataUrlByFile)),
				removeImageIds: post.images
					.filter(x => !existingImages.some(existing => existing.id === x.id))
					.map(x => x.id),
				removeAttachments,
				addAttachments: await Promise.all(
					addAttachments.map(async attachment => ({
						label: attachment.label,
						dataUrl: await getDataUrlByFile(attachment.file),
						extension: getExtensionByFileName(attachment.file.name) ?? "",
					})),
				),
			},
		})
	}

	return (
		<StyledForm onSubmit={handleSubmit}>
			<ModalInputs>
				<InputField label="Title" value={title} onChange={setTitle} />
				{content.element}
				<div>
					<InputLabel>Images</InputLabel>
					<div className="flex flex-wrap gap-2">
						{existingImages.map(image => (
							<div key={`existing-${image.id}`} className="relative">
								<button
									type="button"
									className="absolute top-2 right-2 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70"
									onClick={() => setExistingImages(current => current.filter(x => x.id !== image.id))}
								>
									<DeleteIcon className="text-danger h-4 w-4" />
								</button>
								<img src={image.filePath} alt="Post image" className="h-23 rounded-lg" />
							</div>
						))}
						{uploadedImages.map((image, index) => (
							<UploadedPostImage
								key={`uploaded-${index}`}
								file={image}
								onDelete={() => setUploadedImages(current => removeArrayIndex(current, index))}
							/>
						))}
						<div
							className="border-primary text-primary flex h-23 w-23 cursor-pointer items-center justify-center rounded-lg border border-dashed"
							{...imagesDropzone.getRootProps()}
						>
							<input {...imagesDropzone.getInputProps()} />
							<UploadIcon className="h-6 w-6" />
						</div>
					</div>
				</div>
				<div>
					<InputLabel>Video embed</InputLabel>
					<InputField
						inputType="url"
						placeholder="YouTube or Vimeo URL"
						value={videoUrl}
						onChange={setVideoUrl}
					/>
				</div>
				{post.hasPoll && (
					<p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900">
						Poll options cannot be edited after posting.
					</p>
				)}
				<Dropdown
					label="Category"
					items={[
						{ id: "none", title: "-- No category --" },
						...(categories.map(category => ({
							id: category.id.toString(),
							title: category.label,
						})) ?? []),
						{ id: "create", title: "Create new category" },
					]}
					selectedItemId={categoryId ?? "none"}
					onChange={async id => {
						if (id === "create") {
							const result = await alert.input(
								"Create category",
								"Please enter a name for the category.",
								"Name",
							)
							if (result.result === null) return
							const category = await trpc.community.createPostCategory.mutate(result.result.trim())
							await onReloadCategories()
							result.close()
							setCategoryId(category.id.toString())
							return
						}

						setCategoryId(id === "none" ? null : id)
					}}
				/>
				<div className="flex flex-wrap gap-4">
					{post.attachments
						.filter(x => !removeAttachments.includes(x.id))
						.map((attachment, index) => (
							<div
								key={index}
								className="border-primary relative flex h-24 w-24 cursor-pointer items-center justify-center rounded-lg border border-dashed p-2 break-all"
							>
								<button
									type="button"
									className="absolute top-2 right-2 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70"
									onClick={() => setRemoveAttachments([...removeAttachments, attachment.id])}
								>
									<DeleteIcon className="text-danger h-4 w-4" />
								</button>
								{attachment.label}
							</div>
						))}
					{addAttachments.map((attachment, index) => (
						<div
							key={index}
							className="border-primary relative flex h-24 w-24 cursor-pointer items-center justify-center rounded-lg border border-dashed p-2 break-all"
						>
							<button
								type="button"
								className="absolute top-2 right-2 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70"
								onClick={() =>
									setAddAttachments(current => current.filter(x => x.file !== attachment.file))
								}
							>
								<DeleteIcon className="text-danger h-4 w-4" />
							</button>
							{attachment.label}
						</div>
					))}
					<div
						className="border-primary relative flex h-24 w-24 cursor-pointer items-center justify-center rounded-lg border border-dashed break-all"
						{...dropzone.getRootProps()}
					>
						<input {...dropzone.getInputProps()} />
						<UploadFileIcon className="text-primary h-6 w-6" />
					</div>
				</div>
			</ModalInputs>
			<ModalButtons>
				<Button variant="secondary" isDisabled={save.isPending} onClick={onClose}>
					Cancel
				</Button>
				<StyledSubmitButton variant="primary" isLoading={save.isPending}>
					Save
				</StyledSubmitButton>
			</ModalButtons>
		</StyledForm>
	)
}

const UploadedPostImage: FC<{ file: File; onDelete(): void }> = ({ file, onDelete }) => {
	const url = useFileUrl(file)

	return (
		<div className="relative">
			<button
				type="button"
				className="absolute top-2 right-2 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70"
				onClick={onDelete}
			>
				<DeleteIcon className="text-danger h-4 w-4" />
			</button>
			<img src={url} alt="Post image" className="h-23 rounded-lg" />
		</div>
	)
}

const StyledForm = styled(Form)`
	display: flex;
	flex-direction: column;
	align-items: stretch;
`

const StyledSubmitButton = styled(SubmitButton)`
	align-self: flex-start;
`

export default EditModal
